import React, { createContext, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { notifyIcon } from "../../../../config";
import { getNotification } from "../../../../utils/common";
import { labels } from "./ruleEngine.constants";
import { deleteEmailRules, getEmailRuleActions, getEmailRules, updateEmailRule } from "./rules.service";

const EmailRulesContext = createContext();

export const useEmailRules = () => {
  const context = useContext(EmailRulesContext);
  return context;
};

/**
 * Context provider for email rules functionality
 * Provides state and methods for managing email rules
 * @author Sarthak Arora
 */
export const EmailRulesProvider = ({ children }) => {
  const { workflowId } = useParams();
  const [rules, setRules] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  useEffect(() => {
    fetchEmailRules();
  }, [workflowId]);

  /**
   * Fetches email rules from the API
   * @author Sarthak Arora
   */
  const fetchEmailRules = async () => {
    try {
      setLoading(true);
      const [rulesResponse, actionsResponse] = await Promise.all([getEmailRules(workflowId), getEmailRuleActions()]);

      if (rulesResponse) {
        setRules(rulesResponse);
      }
      if (actionsResponse) {
        setActions(actionsResponse);
      }
    } catch (error) {
      getNotification("Failed to fetch rules", notifyIcon.ERROR_ICON);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Opens the rule form
   * @param {Object} rule - The rule to be edited
   * @author Sarthak Arora
   */
  const openForm = (rule = null) => {
    setEditingRule(rule);
    setIsFormOpen(true);
  };

  /**
   * Closes the rule form
   * @author Sarthak Arora
   */
  const closeForm = () => {
    setIsFormOpen(false);
    setEditingRule(null);
  };

  /**
   * Refreshes the rules
   * @author Sarthak Arora
   */
  const refreshRules = () => {
    fetchEmailRules();
  };

  /**
   * Handles the deletion of a rule
   * @param {number} ruleId - The ID of the rule to be deleted
   * @author Sarthak Arora
   */
  const handleDelete = async (ruleId) => {
    try {
      await deleteEmailRules(ruleId);
      setRules((prevRules) => prevRules.filter((rule) => rule.id !== ruleId));
      closeForm();
      getNotification(labels.NOTIFICATIONS.RULE_DELETE_SUCCESS, notifyIcon.SUCCESS_ICON);
    } catch (error) {
      getNotification(labels.NOTIFICATIONS.RULE_DELETE_FAILURE, notifyIcon.ERROR_ICON);
    }
  };

  /**
   * Handles the update of a rule
   * @param {Object} payload - The payload to be updated
   * @author Sarthak Arora
   */
  const handleUpdate = async (payload) => {
    try {
      await updateEmailRule(payload);
      refreshRules();
      closeForm();
      getNotification(labels.NOTIFICATIONS.RULE_UPDATE_SUCCESS, notifyIcon.SUCCESS_ICON);
    } catch (error) {
      getNotification(labels.NOTIFICATIONS.RULE_UPDATE_FAILURE, notifyIcon.ERROR_ICON);
    }
  };

  const value = {
    rules,
    actions,
    loading,
    error,
    isFormOpen,
    editingRule,
    workflowId,
    setRules,
    openForm,
    closeForm,
    refreshRules,
    setEditingRule,
    setIsFormOpen,
    handleDelete,
    handleUpdate,
  };

  return <EmailRulesContext.Provider value={value}>{children}</EmailRulesContext.Provider>;
};
